import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';

const CREDITS_URL = 'https://sellercentral.amazon.in/gc/seller-university/advanced-learning-path/credits';

export default function SellerDashboard() {
  const [quizzes, setQuizzes] = useState([]);
  const [attempts, setAttempts] = useState({});

  // Use sellerId if available (from seller login), fallback to username for backward compatibility
  const sellerId = localStorage.getItem('sellerId');
  const username = localStorage.getItem('username') || 'Seller';
  const displayName = sellerId ? sellerId.toUpperCase() : username;
const [remainingTime, setRemainingTime] = useState(null);
const [progress, setProgress] = useState(100);
const activeQuiz = quizzes[0];
useEffect(() => {
  if (!activeQuiz) return;

  const limit = getRemainingAttempts(activeQuiz.id);
  const allAttempts = getQuizAttempts(activeQuiz.id);
  const hasPassedAttempt = allAttempts.some((attempt) => attempt?.passed);
  if (!limit.blocked || hasPassedAttempt) {
    setRemainingTime(null);
    setProgress(100);
    return;
  }

  const totalMs = 24 * 60 * 60 * 1000; // 24 hours
  const endTime = new Date(limit.blockUntil).getTime();

  const interval = setInterval(() => {
    const now = Date.now();
    const diff = endTime - now;

    if (diff <= 0) {
      clearInterval(interval);
      setRemainingTime(null);
      setProgress(0);
      return;
    }

    const hrs = Math.floor(diff / (60 * 60000));
    const mins = Math.floor(diff / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    setRemainingTime(
      `${String(hrs).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`
    );

    setProgress((diff / totalMs) * 100);
  }, 1000);

  return () => clearInterval(interval);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeQuiz, attempts]);

  useEffect(() => {
    async function fetchData() {
      try {
        const quizzesRes = await API.get('/quizzes');
        setQuizzes(quizzesRes.data);

        // Fetch attempts - backend should return attempts based on sellerId or username
        const attemptsRes = await API.get('/attempts');
        setAttempts(attemptsRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    }
    fetchData();
  }, []);

  // Safe way to get attempts for a quiz (supports both old object and new array format)
  const getQuizAttempts = (quizId) => {
    const quizData = attempts[quizId];
    if (!quizData) return [];

    if (Array.isArray(quizData)) {
      return quizData;
    }

    // Handle legacy single-object format
    if (typeof quizData === 'object' && quizData !== null) {
      return [{
        score: quizData.score,
        passed: quizData.passed,
        timestamp: quizData.timestamp || new Date().toISOString(),
        date: quizData.date || new Date().toLocaleDateString(),
        time: quizData.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        attemptNumber: 1
      }];
    }

    return [];
  };

  const latestAttempt = (quizId) => {
    const allAttempts = getQuizAttempts(quizId);
    if (allAttempts.length === 0) return null;
    return allAttempts[allAttempts.length - 1];
  };

  const getRemainingAttempts = (quizId) => {
    const allAttempts = getQuizAttempts(quizId);
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const recentAttempts = allAttempts.filter(a => {
      const attemptTime = new Date(a.timestamp);
      return attemptTime > twentyFourHoursAgo;
    });

    if (recentAttempts.length >= 3) {
      const oldestRecentTimestamp = Math.min(...recentAttempts.map(a => new Date(a.timestamp).getTime()));
      const blockUntil = new Date(oldestRecentTimestamp + 24 * 60 * 60 * 1000);
      return { blocked: true, blockUntil };
    }

    return { blocked: false, remaining: 3 - recentAttempts.length };
  };

 // first quiz (same behavior as old)
const quizAttempts = activeQuiz
  ? getQuizAttempts(activeQuiz.id)
  : [];

const totalAttempts = quizAttempts.length;
const bestScore = quizAttempts.length
  ? Math.max(...quizAttempts.map(a => a.score))
  : 0;

const avgScore = quizAttempts.length
  ? Math.round(
      quizAttempts.reduce((s, a) => s + a.score, 0) / quizAttempts.length
    )
  : 0;


return (
  <div className="sd-page">
    {/* HEADER */}
    <header className="amz-header">
      <img className="amz-header-logo" src="/scaling_white_background.png" alt="Amazon" />
      <div className="amz-header-welcome">
        Welcome,&nbsp;<b>{displayName}</b>
      </div>
      <div className="amz-header-actions">
        <button
          className="amz-logout"
          onClick={() => { localStorage.clear(); window.location.href = '/seller-login'; }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Logout
        </button>
      </div>
    </header>

    <div className="sd-layout">
      {/* ── LEFT SIDEBAR ── */}
      <aside className="sd-sidebar">
        {/* Computer Image Card */}
        <div className="sd-sidebar-card sd-sidebar-computer-card">
          <img src="/computer_new.png" alt="" className="sd-sidebar-computer-img" />
        </div>

        {/* Assessment Rules */}
        <div className="sd-sidebar-card">
          <h4 className="sd-sidebar-rules-title">Assessment Rules</h4>
          <ul className="sd-sidebar-rules-list">
            <li><b>Fair use:</b> Attempts may be restricted for misuse or abnormal activity.</li>
            <li><b>Attempts:</b> Up to 3 attempts per 24 hours. If you reach the limit, try again after the cooldown resets.</li>
            <li><b>Passing Criteria:</b> Score 60% or higher.</li>
            <li><b>Credits*</b> are issued only after passing the quiz with minimum 60% score and completing a minimum of 4 videos in the learning-path are verified.</li>
          </ul>
          <p className="sd-rules-note">
            *Additional terms and conditions will apply on{' '}
            <a href={CREDITS_URL} target="_blank" rel="noreferrer" className="sd-rules-note-link">credits</a>.
          </p>
        </div>
      </aside>

      {/* ── MAIN CONTENT ── */}
      <main className="sd-main">
        {/* Title row */}
        <div className="sd-main-header">
          <div>
            <h1 className="sd-main-title">Scaling on <span>Amazon!</span></h1>
            <p className="sd-main-subtitle">Complete the quiz to earn credits</p>
          </div>
        </div>

        {/* KPI Row */}
        <div className="sd-kpi-row">
          {/* Best Score — dark ring chart */}
          {(() => {
            const r = 28;
            const circ = 2 * Math.PI * r;
            const dash = (bestScore / 100) * circ;
            return (
              <div className="sd-kpi-card sd-kpi-card--best">
                <span className="sd-kpi-label">Best Score</span>
                <div className="sd-kpi-ring-wrap">
                  <svg width="72" height="72" viewBox="0 0 72 72">
                    <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="6"/>
                    <circle cx="36" cy="36" r={r} fill="none" stroke="var(--amz-orange)" strokeWidth="6"
                      strokeDasharray={`${dash} ${circ}`}
                      strokeLinecap="round"
                      transform="rotate(-90 36 36)"
                    />
                  </svg>
                  <div className="sd-kpi-ring-inner">
                    <span className="sd-kpi-ring-val">{bestScore}</span>
                  </div>
                </div>
                <p className="sd-kpi-sub">
                  {totalAttempts === 0 ? 'No attempts yet' : bestScore >= 60 ? '🏆 Passed' : 'Keep pushing!'}
                </p>
              </div>
            );
          })()}

          {/* Average Score — mini bar chart */}
          <div className="sd-kpi-card sd-kpi-card--active">
            <span className="sd-kpi-label">Average Score</span>
            <div className="sd-kpi-bigval">
              <span className="sd-kpi-num">{avgScore}</span>
              <span className="sd-kpi-den">/100</span>
            </div>
            <div className="sd-kpi-bars">
              {quizAttempts.length === 0 ? (
                <p className="sd-kpi-sub" style={{ margin: 'auto', alignSelf: 'center' }}>No attempts yet — start your journey!</p>
              ) : (
                quizAttempts.slice(-4).map((a, i) => (
                  <div key={i} className="sd-kpi-bar-wrap">
                    <div
                      className="sd-kpi-bar"
                      style={{ height: `${Math.max(a.score, 5)}%` }}
                      data-pass={String(a.passed)}
                    />
                    <span className="sd-kpi-bar-label">{a.score}%</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Last Score — progress bar */}
          {(() => {
            const lastScore = activeQuiz && latestAttempt(activeQuiz.id)
              ? latestAttempt(activeQuiz.id).score
              : null;
            const lastPassed = activeQuiz && latestAttempt(activeQuiz.id)
              ? latestAttempt(activeQuiz.id).passed
              : null;
            return (
              <div className={`sd-kpi-card${lastPassed === true ? ' sd-kpi-card--last-pass' : lastPassed === false ? ' sd-kpi-card--last-fail' : ''}`}>
                <span className="sd-kpi-label">Last Score</span>
                <div className="sd-kpi-bigval">
                  <span className="sd-kpi-num">{lastScore != null ? lastScore : '—'}</span>
                  {lastScore != null && <span className="sd-kpi-den">/100</span>}
                </div>
                <div className="sd-kpi-progress-bar">
                  <div className="sd-kpi-progress-fill" style={{ width: `${lastScore || 0}%` }} />
                </div>
                <p className="sd-kpi-sub">
                  {lastScore == null ? 'No attempt yet' : lastPassed ? 'Passed ✓' : 'Not passed yet'}
                </p>
              </div>
            );
          })()}
        </div>

        {/* Bottom grid */}
        <div className="sd-bottom-row">
          {/* Attempt History */}
          <div className="sd-card">
            <div className="sd-card-header">
              <div className="sd-card-header-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 className="sd-card-title">Attempt History</h3>
              {activeQuiz && <span className="sd-history-quiz-tag">{activeQuiz.title}</span>}
            </div>
            <div className="sd-history-bar-chart">
              {Array.from({ length: 3 }).map((_, i) => {
                const attempt = quizAttempts[i];
                const attemptNumber = i + 1;
                const score = attempt ? (attempt.score != null ? attempt.score : null) : null;
                const passed = attempt ? attempt.passed : null;
                const status = !attempt ? null : attempt.score == null ? 'Pending' : attempt.passed ? 'Pass' : 'Try Again';
                const badgeClass = status === 'Pass' ? 'sd-badge-pass' : status === 'Try Again' ? 'sd-badge-fail' : status === 'Pending' ? 'sd-badge-pending' : '';
                return (
                  <div key={i} className={`sd-history-bar-col${!attempt ? ' empty' : ''}`}>
                    <span className={`sd-history-bar-score${passed === true ? ' pass' : passed === false ? ' fail' : ''}`}>
                      {score !== null ? `${score}%` : '—'}
                    </span>
                    <div className="sd-history-bar-track">
                      <div
                        className={`sd-history-bar-fill${passed === true ? ' pass' : passed === false ? ' fail' : ''}`}
                        style={{ height: `${score !== null ? Math.max(score, 4) : 0}%` }}
                      />
                    </div>
                    <span className="sd-history-bar-attempt">Attempt {attemptNumber}</span>
                    {status && <span className={`sd-badge ${badgeClass}`}>{status}</span>}
                  </div>
                );
              })}
            </div>
          </div>


          <div className="sd-card sd-quiz-card">

          <h2 className="sd-quiz-name">{activeQuiz?.title || '\u2014'}</h2>
          <p className="sd-quiz-subtitle">Test your knowledge and earn credits</p>

          <div className="sd-quiz-badge">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M10 2l2.47 5.01L18 7.74l-4 3.9.94 5.5L10 14.27l-4.94 2.87.94-5.5-4-3.9 5.53-.73L10 2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Unlock credits worth up to ₹1 Lakh
          </div>

          <div className="sd-quiz-divider" />

          {activeQuiz && (() => {
            const limit = getRemainingAttempts(activeQuiz.id);
            const allAttempts = getQuizAttempts(activeQuiz.id);
            const latest = allAttempts.length ? allAttempts[allAttempts.length - 1] : null;
            const hasPassedAttempt = allAttempts.some((attempt) => attempt?.passed);

            if (!hasPassedAttempt && !limit.blocked) {
              return (
                <Link to={`/quiz/${activeQuiz.id}`} style={{ textDecoration: 'none' }}>
                  <button className="amz-btn amz-btn-primary amz-btn-lg">
                    {latest ? 'RE-ATTEMPT QUIZ' : 'START QUIZ'}
                    <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </Link>
              );
            }

            if (hasPassedAttempt) {
              return (
                <button className="amz-btn amz-btn-success amz-btn-lg">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2l2.47 5.01L18 7.74l-4 3.9.94 5.5L10 14.27l-4.94 2.87.94-5.5-4-3.9 5.53-.73L10 2z" fill="#FFD700" stroke="#FFD700" strokeWidth="1"/>
                  </svg>
                  Congratulations! You Passed
                </button>
              );
            }

            return null;
          })()}

          {activeQuiz && (() => {
            const limit = getRemainingAttempts(activeQuiz.id);
            const allAttempts = getQuizAttempts(activeQuiz.id);
            const hasPassedAttempt = allAttempts.some((attempt) => attempt?.passed);
            if (limit.blocked && !hasPassedAttempt) {
              return (
                <button className="amz-btn amz-btn-secondary amz-btn-lg" disabled>
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                    <rect x="5" y="9" width="10" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 9V6a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  Daily attempts limit reached
                </button>
              );
            }
            return null;
          })()}

          <div className="sd-quiz-divider" />

          {/* Attempt dots */}
          {activeQuiz && (() => {
            const limit = getRemainingAttempts(activeQuiz.id);
            const used = limit.blocked ? 3 : 3 - limit.remaining;
            const dots = [];
            for (let i = 0; i < 3; i++) {
              const isUsed = i < used;
              dots.push(
                <span key={i} className={`sd-attempt-dot${isUsed ? ' used' : ''}`}>
                  {isUsed ? (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="#B12704" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M4 8l3 3 5-5" stroke="#FF9900" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
              );
            }
            return (
              <div className="sd-attempts-row">
                <div className="sd-attempts-dots">{dots}</div>
                <span className="sd-attempts-label">Attempts Remaining Today</span>
              </div>
            );
          })()}

          {/* Timer */}
          {activeQuiz && (() => {
            const limit = getRemainingAttempts(activeQuiz.id);
            const allAttempts = getQuizAttempts(activeQuiz.id);
            const hasPassedAttempt = allAttempts.some((attempt) => attempt?.passed);
            if (!limit.blocked || hasPassedAttempt || !remainingTime) return null;

            return (
              <div className="sd-timer">
                Try again after {remainingTime} at{' '}
                <b>
                  {limit.blockUntil.toLocaleTimeString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </b>
                <div className="sd-timer-bar">
                  <div className="sd-timer-fill" style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })()}
        </div>

        </div>
      </main>
    </div>

    {/* ── FOOTER ── */}
    <footer className="sd-footer">
      © {new Date().getFullYear()} Scaling on Amazon · All rights reserved · Passing score: 60% · Credits subject to T&amp;C
    </footer>
  </div>
);
}
